import { Component, ElementRef, ViewChild, Input, OnInit, Output, EventEmitter} from '@angular/core';
import { Validators, AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormGroup, UntypedFormBuilder} from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';
import { ToastMessageService } from 'src/app/widgets/toast-message/toast-message.service';

@Component({
  selector: 'search-barcode-model',
  templateUrl: './search-barcode-model.component.html',
  styleUrls: ['./search-barcode-model.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SearchBarcodeModelComponent,
      multi: true
    }
  ]
})
export class SearchBarcodeModelComponent implements OnInit, ControlValueAccessor {

  @ViewChild('input', {static: true}) input: ElementRef;

  // inputs
  @Input() additionalProcessing: boolean;
  @Input() errors: any;
  @Input() fieldError: string;
  @Input() isDisabled: boolean;
  @Input() isInvalid: boolean;
  @Input() label: string;
  @Input() maxlength: string;
  @Input() placeholder: string = '';
  @Input() class: string;
  @Input() required: boolean;
  @Input() showGroups: boolean = false;
  @Input() customSearchFunction?: (searchValue: string) => void;

  @Output() findItem: EventEmitter<any> = new EventEmitter<any>();

  public formProcessing: boolean = false;
  public value: string;
  public changed: (value: string) => void;
  public touched: () => void;
  onChangeSubs: Subscription[] = [];

  constructor(
      private _fb: UntypedFormBuilder,
      private _apiSvc: ApiService,
      private _toastMsgSvc: ToastMessageService
    ) { }

  // from group
  form: UntypedFormGroup = this._fb.group({
    searchInput: [null]
  })

  private modelsEp: string = `${this._apiSvc.endpoints.catalog}/api/ProductModel`;

  ngOnDestroy() {
    for (let sub of this.onChangeSubs) {
      sub.unsubscribe();
    }
  }
  
  ngOnInit(): void {

    this.form.controls.searchInput.valueChanges
      .pipe(debounceTime(2000))
      .subscribe((value) => {
        if (value) {
          const searchValue = this.form.get("searchInput").value;
          if (searchValue) {
            if (this.customSearchFunction) {
              this.customSearchFunction(searchValue);
            }
            else {
              this.searchModelsByBarcode(searchValue);
            }
          }
        }
      });

    this.form.controls.searchInput.valueChanges
      .subscribe((val)=> {
        this.onTextChange(val);
      }
    );
  }

  clearSearchInput(): void {
    this.form.get('searchInput').setValue(null);
  }

  onBarcodeSearchResult(result: any): void {
    this.findItem.emit(result);
  }

  searchModelsByBarcode(barcode: string) {
    this.formProcessing = true;
    this._apiSvc.search(
      this.modelsEp,
      {
        Filter: `BarCode~eq~'` + barcode.trim() + `'`, sort: 'Id-asc'
      }
    )
      .subscribe((res: any) => {
        let modelResult: any = null;
        if (res && res.Data[0]) {
          if(res.Data[0].Type == 3 && !this.showGroups){
            this.showToastMessage('Cannot scan groups.', 'error');
            this.clearSearchInput();
          }
          else{
            modelResult = res.Data[0];
            this.showToastMessage('Item found!');
          }
        }
        else {
          this.clearSearchInput();
          this.showToastMessage('Item not found!', 'error');
        }

        this.onBarcodeSearchResult(modelResult);
        this.formProcessing = false;
      }, () => {
        this.showToastMessage('Item not found!', 'error');
        this.formProcessing = false;
      }
    );
  }
  
  showToastMessage(msg: string, msgType: string = 'success') {
    this._toastMsgSvc.send({
      duration: msgType === 'error' ? 6000 : 3000,
      type: msgType,
      message: msg,
      icon: 'check_circle_outline'
    });
  }

  onTextChange(value: string) {
    this.value = value;
    this.changed(this.value);
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.changed = fn;
  }

  registerOnTouched(fn: () => void) {
    this.touched = fn;
  }

  setDisabledState(disabled: boolean): void {
    if (disabled) {
      this.form.disable();
    }
    else {
      this.form.enable();
    }
  }

  validate(ctrl: AbstractControl) {
    return Validators.required(ctrl);
  }

}
